import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import type { BannerState } from "../../hooks/useAudioOnlyMode";

// ── Custom icons — unfilled, rounded strokes ──────────────────────────────────

/**
 * Wifi arcs with an exclamation mark — unstable connection.
 * viewBox 0 0 24 20 gives natural wifi proportions (wider than tall).
 */
const WifiWarningIcon = ({ color }: { color: string }) => (
  <svg width="18" height="15" viewBox="0 0 24 20" fill="none" aria-hidden="true">
    {/* Outer arc */}
    <path d="M1.5 7 Q12 1.5 22.5 7"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {/* Middle arc */}
    <path d="M5 10.5 Q12 5.5 19 10.5"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {/* Inner arc — left half, meets exclamation at top centre */}
    <path d="M8.5 14 Q10.25 11 12 11"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {/* Inner arc — right half */}
    <path d="M12 11 Q13.75 11 15.5 14"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {/* Exclamation stem */}
    <line x1="12" y1="12" x2="12" y2="16" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    {/* Exclamation dot */}
    <circle cx="12" cy="18" r="0.95" fill={color} />
  </svg>
);

/**
 * Wifi with outer + middle arcs faded — dropped / no connection.
 * Same viewBox as WifiWarningIcon for consistency.
 */
const WifiDroppedIcon = ({ color }: { color: string }) => (
  <svg width="18" height="15" viewBox="0 0 24 20" fill="none" aria-hidden="true">
    {/* Outer arc — faded */}
    <path d="M1.5 7 Q12 1.5 22.5 7"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.25" />
    {/* Middle arc — faded */}
    <path d="M5 10.5 Q12 5.5 19 10.5"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.25" />
    {/* Inner arc — full opacity */}
    <path d="M8.5 14 Q12 10.5 15.5 14"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    {/* Base dot */}
    <circle cx="12" cy="17.5" r="1.2" fill={color} />
  </svg>
);

interface ConnectionBannerProps {
  state: BannerState;
}

// ── Animations ────────────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(calc(100% + 24px)); opacity: 0; }
  to   { transform: translateX(0);                 opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0);                 opacity: 1; }
  to   { transform: translateX(calc(100% + 24px)); opacity: 0; }
`;

// ── Toast configs — avatar_unavailable has no toast (silent fallback) ─────────

const TOAST_CONFIGS: Record<
  "pre_call_degraded" | "mid_call_instability" | "active_audio_connection",
  { border: string; color: string; bg: string; icon: React.ReactNode; message: string }
> = {
  pre_call_degraded: {
    bg: "rgba(20, 10, 40, 0.75)",
    border: "rgba(247, 144, 9, 0.45)",
    color: "#F79009",
    icon: <WifiWarningIcon color="#F79009" />,
    message: "Your connection looks unstable — switching to a lighter call experience for now.",
  },
  active_audio_connection: {
    bg: "rgba(20, 10, 40, 0.75)",
    border: "rgba(247, 144, 9, 0.45)",
    color: "#F79009",
    icon: <WifiWarningIcon color="#F79009" />,
    message: "Your connection looks unstable — switching to a lighter call experience for now.",
  },
  mid_call_instability: {
    bg: "rgba(20, 10, 40, 0.75)",
    border: "rgba(240, 68, 56, 0.45)",
    color: "#F04438",
    icon: <WifiDroppedIcon color="#F04438" />,
    message: "Your connection's dropped — adjusting your call to keep things smooth.",
  },
};

const DISPLAY_DURATION_MS = 5000;  // how long the toast stays visible
const EXIT_DURATION_MS    = 400;   // how long the slide-out animation takes

/**
 * Toast-style connection notification.
 * Slides in from the right, auto-dismisses after a few seconds, then slides back out.
 * Avatar-unavailable state is handled silently (no toast shown).
 */
const ConnectionBanner: React.FC<ConnectionBannerProps> = ({ state }) => {
  const [visible, setVisible]   = useState(false);
  const [leaving, setLeaving]   = useState(false);
  const hideTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Which state should actually show a toast (avatar state is silent)
  const toastState =
    state && state !== "active_audio_avatar"
      ? (state as keyof typeof TOAST_CONFIGS)
      : null;

  useEffect(() => {
    // Clear any running timers
    if (hideTimer.current)  clearTimeout(hideTimer.current);
    if (leaveTimer.current) clearTimeout(leaveTimer.current);

    if (!toastState) {
      setVisible(false);
      setLeaving(false);
      return;
    }

    // Show toast
    setLeaving(false);
    setVisible(true);

    // After DISPLAY_DURATION_MS, trigger exit animation
    hideTimer.current = setTimeout(() => {
      setLeaving(true);
      // After animation completes, fully hide
      leaveTimer.current = setTimeout(() => {
        setVisible(false);
        setLeaving(false);
      }, EXIT_DURATION_MS);
    }, DISPLAY_DURATION_MS);

    return () => {
      if (hideTimer.current)  clearTimeout(hideTimer.current);
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    };
  }, [toastState]);

  if (!visible || !toastState) return null;

  const config = TOAST_CONFIGS[toastState];

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "absolute",
        top: "72px",
        right: "20px",
        zIndex: 9998,
        maxWidth: "300px",
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        px: "14px",
        py: "10px",
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        borderRadius: "10px",
        backdropFilter: "blur(14px)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
        animation: leaving
          ? `${slideOut} ${EXIT_DURATION_MS}ms ease-in forwards`
          : `${slideIn} 0.35s ease-out`,
        pointerEvents: "none",
      }}
    >
      {/* Icon */}
      <Box sx={{ color: config.color, display: "flex", alignItems: "center", pt: "1px", flexShrink: 0 }}>
        {config.icon}
      </Box>

      {/* Message */}
      <Typography
        sx={{
          color: "rgba(255,255,255,0.88)",
          fontSize: "0.72rem",
          fontFamily: "Poppins, Helvetica",
          fontWeight: 400,
          letterSpacing: "0.01em",
          lineHeight: 1.5,
        }}
      >
        {config.message}
      </Typography>

      {/* Colour accent bar on left edge */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: "10%",
          height: "80%",
          width: "3px",
          borderRadius: "0 3px 3px 0",
          backgroundColor: config.color,
        }}
      />
    </Box>
  );
};

export default ConnectionBanner;
